// Admin console: Customers list — every tenant plus its current plan
// assignment and a bucket/byte summary, in one screen. See
// docs/architecture/admin-console-concept.md §4.
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { adminApi } from '../api/adminClient';
import { useAsync } from '../lib/hooks';
import { useLang } from '../lib/useLang';
import { formatBytes, formatNumber } from '../lib/format';
import { PageHeader } from '../components/PageHeader';
import { ApiState, EmptyState } from '../components/States';
import { Badge, Button, Card } from '../components/ui';
import { IconPlus, IconSearch, IconUsers, IconWarning } from '../components/Icons';
import { CreateTenantDialog } from './AdminCreateTenant';

function statusTone(status?: string): 'success' | 'warning' | 'default' {
  if (status === 'active') return 'success';
  if (status === 'paused' || status === 'cancelled') return 'warning';
  return 'default';
}

export function AdminCustomers() {
  const { t } = useTranslation();
  const lang = useLang();
  const [q, setQ] = useState('');
  const [creating, setCreating] = useState(false);
  const list = useAsync(() => adminApi.adminTenants(), []);

  const filtered = useMemo(() => {
    const items = list.data?.items ?? [];
    const needle = q.trim().toLowerCase();
    if (!needle) return items;
    return items.filter(
      (c) => c.name.toLowerCase().includes(needle) || c.id.toLowerCase().includes(needle),
    );
  }, [list.data, q]);

  return (
    <>
      <PageHeader
        title={t('admin.customers.title')}
        subtitle={t('admin.customers.subtitle')}
        actions={
          <Button variant="primary" onClick={() => setCreating(true)}>
            <IconPlus width={18} height={18} />
            {t('admin.customers.create')}
          </Button>
        }
      />

      {creating ? (
        <CreateTenantDialog
          onClose={() => setCreating(false)}
          onCreated={() => {
            setCreating(false);
            list.reload();
          }}
        />
      ) : null}

      <Card>
        <div className="row" style={{ padding: '12px 16px' }}>
          <span className="input" style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
            <IconSearch width={16} height={16} />
            <input
              style={{ border: 'none', outline: 'none', flex: 1, background: 'transparent' }}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t('admin.customers.filterPlaceholder')}
              aria-label={t('admin.customers.filterPlaceholder')}
            />
          </span>
        </div>
        <ApiState status={list.status} error={list.error} data={list.status === 'error' ? null : filtered} onRetry={list.reload}>
          {(items) =>
            items.length === 0 ? (
              <EmptyState title={t('admin.customers.empty')} icon={<IconUsers width={26} height={26} />} />
            ) : (
              <div className="table-wrap">
                <table className="data">
                  <thead>
                    <tr>
                      <th>{t('common.name')}</th>
                      <th>{t('admin.customers.colPlan')}</th>
                      <th>{t('admin.customers.colStatus')}</th>
                      <th className="num">{t('admin.customers.colBuckets')}</th>
                      <th className="num">{t('common.size')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((c) => (
                      <tr key={c.id}>
                        <td className="cell-strong">
                          <span className="row" style={{ gap: 6, alignItems: 'center' }}>
                            <Link to={`/customers/${encodeURIComponent(c.id)}`}>{c.name}</Link>
                            {c.rateLimit?.warn ? (
                              <Badge tone={c.rateLimit.overLimit ? 'danger' : 'warning'}>
                                <IconWarning width={13} height={13} />{' '}
                                {c.rateLimit.recentRefusals > 0
                                  ? t('admin.customers.refused', { count: c.rateLimit.recentRefusals })
                                  : t(
                                      c.rateLimit.overLimit
                                        ? 'admin.customers.overLimit'
                                        : 'admin.customers.nearLimit',
                                      { percent: Math.round(c.rateLimit.percent * 100) },
                                    )}
                              </Badge>
                            ) : null}
                          </span>
                        </td>
                        <td>
                          {c.planName ? `${c.planName} (v${c.planVersion})` : t('admin.customers.noPlan')}
                        </td>
                        <td>
                          {c.status ? <Badge tone={statusTone(c.status)}>{t(`admin.status.${c.status}`)}</Badge> : '—'}
                        </td>
                        <td className="num">{formatNumber(c.archiveCount, lang)}</td>
                        <td className="num">{formatBytes(c.bytes, lang)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          }
        </ApiState>
      </Card>
    </>
  );
}
